import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Badge, Button, Select } from '@silverfalls/sterling';
import {
  fetchVendors1099Report,
  type Vendor1099ReportRow,
} from '../api/p2p.js';

interface Report1099Props {
  readonly opcoRef: string;
}

const CURRENT_YEAR = new Date().getUTCFullYear();
// Year selector spans last 5 years through current. Default = previous year
// (matches the typical 1099 cycle: e.g. filing for 2025 throughout Q1 2026).
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const DEFAULT_YEAR = CURRENT_YEAR - 1;

export function Report1099({ opcoRef }: Report1099Props): JSX.Element {
  const [taxYear, setTaxYear] = useState<number>(DEFAULT_YEAR);
  const [rows, setRows] = useState<ReadonlyArray<Vendor1099ReportRow>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchVendors1099Report(opcoRef, taxYear);
      setRows(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef, taxYear]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = useMemo(() => {
    const totalAmount = rows.reduce((sum, r) => sum + Number(r.totalPaid), 0);
    const totalInvoices = rows.reduce((sum, r) => sum + r.invoiceCount, 0);
    const reportable = rows.filter((r) => Number(r.totalPaid) >= 600);
    return {
      vendorCount: rows.length,
      reportableCount: reportable.length,
      totalAmount: totalAmount.toFixed(2),
      totalInvoices,
    };
  }, [rows]);

  const onExportCsv = useCallback(() => {
    const header = [
      'vendorRef',
      'legalName',
      'taxId',
      'addressLine1',
      'addressLine2',
      'city',
      'region',
      'postalCode',
      'country',
      'formType',
      'taxYear',
      'totalPaid',
      'invoiceCount',
      'meetsThreshold',
    ];
    const escape = (v: string) =>
      /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const lines = rows.map((r) =>
      [
        r.vendor.vendorRef,
        r.vendor.legalName,
        r.vendor.taxId,
        r.vendor.addressLine1,
        r.vendor.addressLine2 ?? '',
        r.vendor.city,
        r.vendor.region,
        r.vendor.postalCode,
        r.vendor.country,
        r.formType,
        String(r.taxYear),
        r.totalPaid,
        String(r.invoiceCount),
        Number(r.totalPaid) >= 600 ? 'Y' : 'N',
      ]
        .map(escape)
        .join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `anvil-1099-report-${opcoRef}-${taxYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [rows, opcoRef, taxYear]);

  return (
    <section className="vendors-screen">
      <div className="screen-header">
        <div className="screen-header-left">
          <h2>1099 reporting</h2>
          <Badge variant="info" dot={false}>{opcoRef}</Badge>
        </div>
        <div className="header-right-stack" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            value={String(taxYear)}
            onValueChange={(v) => setTaxYear(Number(v))}
            aria-label="Tax year"
            options={YEAR_OPTIONS.map((y) => ({ value: String(y), label: String(y) }))}
          />
          <Button
            variant="secondary"
            icon="download"
            onClick={onExportCsv}
            disabled={rows.length === 0 || loading}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <p className="t-body-sm">
        Aggregated from APPROVED AP invoices in {taxYear}. Vendors with
        form-1099 status REQUIRED appear regardless of invoice count.
        Threshold flag uses the standard $600 USD-equivalent cutoff. Numbers
        are read-side approximations until §7 payment processing ships
        actually-paid amounts.
      </p>

      {error && (
        <div className="alert alert-neg" role="alert">
          Couldn't load report: {error}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>
            No 1099-required vendors in {opcoRef}. Add or update vendor
            payment profiles to set <span className="mono">form1099Status</span>
            to <span className="mono">REQUIRED</span>.
          </p>
        </div>
      ) : (
        <>
          <div className="detail-grid">
            <div className="detail-card">
              <h3 className="detail-card-title">Summary</h3>
              <div className="detail-rows">
                <DetailRow
                  label="Vendors (1099-required)"
                  value={String(summary.vendorCount)}
                  mono
                />
                <DetailRow
                  label={`Meeting $600 threshold`}
                  value={String(summary.reportableCount)}
                  mono
                />
                <DetailRow
                  label="Total invoices"
                  value={String(summary.totalInvoices)}
                  mono
                />
                <DetailRow
                  label="Total approved $"
                  value={summary.totalAmount}
                  mono
                />
              </div>
            </div>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Tax ID</th>
                <th>Address</th>
                <th>Form</th>
                <th>Invoices</th>
                <th>Total paid</th>
                <th>≥ $600?</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meetsThreshold = Number(r.totalPaid) >= 600;
                return (
                  <tr key={r.vendor.vendorRef}>
                    <td>
                      <div className="cell-primary">{r.vendor.legalName}</div>
                      <div className="cell-secondary mono">{r.vendor.vendorRef}</div>
                    </td>
                    <td className="mono">{r.vendor.taxId}</td>
                    <td className="t-body-sm">
                      {r.vendor.addressLine1}
                      {r.vendor.addressLine2 ? `, ${r.vendor.addressLine2}` : ''}
                      {`, ${r.vendor.city}, ${r.vendor.region} ${r.vendor.postalCode}`}
                    </td>
                    <td className="mono">{r.formType}</td>
                    <td className="mono">{r.invoiceCount}</td>
                    <td className="mono">{r.totalPaid}</td>
                    <td>
                      <Badge variant={meetsThreshold ? 'warning' : 'neutral'} dot={false}>
                        {meetsThreshold ? 'YES' : 'no'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}) {
  return (
    <div className="detail-row">
      <span className="detail-row-label">{label}</span>
      <span className={`detail-row-value${mono ? ' mono' : ''}`}>{value}</span>
    </div>
  );
}
